import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown, Menu, Modal, InputNumber } from 'antd';
import { useSelector } from 'react-redux';
import {
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    INDENT_CONTENT_COMMAND,
    OUTDENT_CONTENT_COMMAND,
    $getSelection,
    $isRangeSelection,
    $createParagraphNode,
    $isParagraphNode,
    $isTextNode,
} from 'lexical';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    $isListNode,
    ListNode,
} from '@lexical/list';
import {
    $createHeadingNode,
    $isHeadingNode,
    $isQuoteNode,
} from '@lexical/rich-text';
import {
    $setBlocksType,
    $patchStyleText,
    $getSelectionStyleValueForProperty,
} from '@lexical/selection';
import { $isTableSelection } from '@lexical/table';
import {
    $getNearestNodeOfType,
    $getNearestBlockElementAncestorOrThrow,
} from '@lexical/utils';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import {
    FormatPainterOutlined,
    RightOutlined,
    CheckOutlined,
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    VerticalAlignTopOutlined,
    VerticalAlignBottomOutlined,
    FontSizeOutlined,
    ProfileOutlined,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    MenuOutlined,
    MenuUnfoldOutlined,
    MenuFoldOutlined,
    ColumnHeightOutlined,
    VerticalAlignMiddleOutlined,
    OrderedListOutlined,
    UnorderedListOutlined,
    ClearOutlined,
} from '@ant-design/icons';
import {
    $createExtendedParagraphNode,
} from '../../lexical/nodes/ExtendedParagraphNode';
import {
    $createExtendedHeadingNode,
} from '../../lexical/nodes/ExtendedHeadingNode';
import {
    getBlockStyleObject,
    blockStyleToCss,
} from '../../lexical/nodes/blockStyle';
import './style.css';

const PARAGRAPH_STYLES = [
    { key: 'paragraph', label: 'Normal text' },
    { key: 'h1', label: 'Heading 1' },
    { key: 'h2', label: 'Heading 2' },
    { key: 'h3', label: 'Heading 3' },
];

const LINE_SPACING_OPTIONS = [
    { key: '1.0', label: 'Single' },
    { key: '1.15', label: '1.15' },
    { key: '1.5', label: '1.5' },
    { key: '2.0', label: 'Double' },
];

// Default amount applied by the quick "Add space before/after paragraph" toggles.
const DEFAULT_PARAGRAPH_SPACE = '12px';

const DEFAULT_STATE = {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    isStrikethrough: false,
    isSubscript: false,
    isSuperscript: false,
    blockType: 'paragraph',
    elementFormat: 'left',
    lineSpacing: '',
    spaceBefore: '',
    spaceAfter: '',
};

// Renders a menu row matching the shared `.menu-dropdown` look (icon | text | optional check).
const menuItem = (icon, text, active) => (
    <span className="menu-dropdown-item">
        <span className="menu-dropdown-item-left">{icon}</span>
        <span className="menu-dropdown-item-text">{text}</span>
        {active && (
            <span className="menu-dropdown-item-check"><CheckOutlined /></span>
        )}
    </span>
);

const submenuTitle = (icon, text) => (
    <span className="menu-dropdown-item">
        <span className="menu-dropdown-item-left">{icon}</span>
        <span className="menu-dropdown-item-text">{text}</span>
        <span className="menu-dropdown-item-arrow"><RightOutlined /></span>
    </span>
);

// Resolves the paragraph/heading block that owns the given node, or null for
// blocks we don't apply spacing to (list items, table cells, etc.).
function $getSpacingBlock(node) {
    let block;
    try {
        block = $getNearestBlockElementAncestorOrThrow(node);
    } catch (e) {
        return null;
    }
    if ($isParagraphNode(block) || $isHeadingNode(block)) {
        return block;
    }
    return null;
}

// Ensures the block supports a block-level style. Stock paragraphs/headings
// created during the session (before any reload) are upgraded in place.
function $ensureStyledBlock(block) {
    if (typeof block.setStyle === 'function' && typeof block.getStyle === 'function') {
        return block;
    }
    let replacement;
    if ($isHeadingNode(block)) {
        replacement = $createExtendedHeadingNode(block.getTag());
    } else if ($isParagraphNode(block)) {
        replacement = $createExtendedParagraphNode();
        if (block.getTextFormat && replacement.setTextFormat) {
            replacement.setTextFormat(block.getTextFormat());
        }
    } else {
        return null;
    }
    replacement.setFormat(block.getFormatType());
    replacement.setIndent(block.getIndent());
    const dir = block.getDirection();
    if (dir) replacement.setDirection(dir);
    block.replace(replacement, true);
    return replacement;
}

// Recursively strips inline style + format from every text node inside an
// element. Used by "Clear formatting" when the caret is collapsed, so the whole
// paragraph (including text-level line spacing) is cleared.
function $clearTextFormattingIn(node) {
    if ($isTextNode(node)) {
        if (node.__style !== '') node.setStyle('');
        if (node.__format !== 0) node.setFormat(0);
        return;
    }
    if (typeof node.getChildren === 'function') {
        for (const child of node.getChildren()) {
            $clearTextFormattingIn(child);
        }
    }
}

const FormatMenu = () => {
    const editor = useSelector((state) => state.editor.lexicalEditor);
    const isReadOnly = useSelector((state) => state.editor.isReadOnly);
    const [formatMenuVisible, setFormatMenuVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [state, setState] = useState(DEFAULT_STATE);
    const [customModalVisible, setCustomModalVisible] = useState(false);
    const [customLineHeight, setCustomLineHeight] = useState(null);
    const [customBefore, setCustomBefore] = useState(null);
    const [customAfter, setCustomAfter] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Read the current selection's formatting so active options can be checked,
    // mirroring how the editor toolbar derives its own active state.
    const readActiveState = useCallback(() => {
        if (!editor) return;
        editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                setState(DEFAULT_STATE);
                return;
            }

            const anchorNode = selection.anchor.getNode();

            let blockType = 'paragraph';
            let elementFormat = 'left';
            let spaceBefore = '';
            let spaceAfter = '';

            const listNode = $getNearestNodeOfType(anchorNode, ListNode);
            const block = $getSpacingBlock(anchorNode);

            if (listNode) {
                blockType = listNode.getListType();
            } else if (block) {
                // ExtendedParagraphNode reports type "extended-paragraph"; treat it
                // as a normal paragraph for the "Normal text" check.
                const rawType = $isHeadingNode(block)
                    ? block.getTag()
                    : block.getType();
                blockType = rawType === 'extended-paragraph' ? 'paragraph' : rawType;
            }

            if (block) {
                elementFormat = block.getFormatType() || 'left';
                if (typeof block.getStyle === 'function') {
                    const styleObj = getBlockStyleObject(block.getStyle());
                    spaceBefore = styleObj['margin-top'] || '';
                    spaceAfter = styleObj['margin-bottom'] || '';
                }
            }

            setState({
                isBold: selection.hasFormat('bold'),
                isItalic: selection.hasFormat('italic'),
                isUnderline: selection.hasFormat('underline'),
                isStrikethrough: selection.hasFormat('strikethrough'),
                isSubscript: selection.hasFormat('subscript'),
                isSuperscript: selection.hasFormat('superscript'),
                blockType,
                elementFormat,
                lineSpacing: $getSelectionStyleValueForProperty(
                    selection,
                    'line-height',
                    '',
                ),
                spaceBefore,
                spaceAfter,
            });
        });
    }, [editor]);

    const onVisibleChange = (visible) => {
        if (visible) {
            readActiveState();
        }
        setFormatMenuVisible(visible);
    };

    const setBlock = useCallback(
        (type) => {
            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;
                $setBlocksType(selection, () =>
                    type === 'paragraph'
                        ? $createParagraphNode()
                        : $createHeadingNode(type),
                );
            });
        },
        [editor],
    );

    const toggleList = useCallback(
        (listType) => {
            const isActive = state.blockType === listType;
            if (isActive) {
                editor.update(() => {
                    const selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                        $setBlocksType(selection, () => $createParagraphNode());
                    }
                });
                return;
            }
            editor.dispatchCommand(
                listType === 'number'
                    ? INSERT_ORDERED_LIST_COMMAND
                    : INSERT_UNORDERED_LIST_COMMAND,
                undefined,
            );
        },
        [editor, state.blockType],
    );

    const setLineSpacing = useCallback(
        (value) => {
            editor.update(() => {
                const selection = $getSelection();
                if (selection !== null) {
                    $patchStyleText(selection, { 'line-height': value });
                }
            });
        },
        [editor],
    );

    // Applies/removes block-level spacing (margin-top / margin-bottom) on every
    // paragraph or heading covered by the selection. A value of '' clears it.
    const applyBlockSpacing = useCallback(
        (patch) => {
            editor.update(() => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection)) return;
                const selectedNodes = selection.getNodes();
                const nodes = selectedNodes.length
                    ? selectedNodes
                    : [selection.anchor.getNode()];
                const blocks = new Map();
                for (const node of nodes) {
                    const block = $getSpacingBlock(node);
                    if (block) blocks.set(block.getKey(), block);
                }
                for (const block of blocks.values()) {
                    const styled = $ensureStyledBlock(block);
                    if (!styled) continue;
                    const styleObj = getBlockStyleObject(styled.getStyle());
                    for (const [prop, val] of Object.entries(patch)) {
                        if (val === '' || val == null) delete styleObj[prop];
                        else styleObj[prop] = val;
                    }
                    styled.setStyle(blockStyleToCss(styleObj));
                }
            });
        },
        [editor],
    );

    // Clears inline text formatting (like the toolbar) and additionally resets
    // block-level formatting added by this menu: paragraph spacing, alignment,
    // and heading/quote -> normal. Works on a real selection or just a caret.
    const clearFormatting = useCallback(() => {
        editor.update(() => {
            const selection = $getSelection();
            if (!($isRangeSelection(selection) || $isTableSelection(selection))) {
                return;
            }

            const anchor = selection.anchor;
            const focus = selection.focus;
            const nodes = selection.getNodes();
            const extractedNodes = selection.extract();
            const isCollapsed =
                anchor.key === focus.key && anchor.offset === focus.offset;

            // Inline (text-node) formatting can only be split/cleared precisely
            // when there's an actual selection range.
            if (!isCollapsed) {
                nodes.forEach((node, idx) => {
                    if ($isTextNode(node)) {
                        let textNode = node;
                        if (idx === 0 && anchor.offset !== 0) {
                            textNode = textNode.splitText(anchor.offset)[1] || textNode;
                        }
                        if (idx === nodes.length - 1) {
                            textNode = textNode.splitText(focus.offset)[0] || textNode;
                        }
                        const extractedTextNode = extractedNodes[0];
                        if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
                            textNode = extractedTextNode;
                        }

                        if (textNode.__style !== '') {
                            textNode.setStyle('');
                        }
                        if (textNode.__format !== 0) {
                            textNode.setFormat(0);
                        }
                    } else if ($isDecoratorBlockNode(node)) {
                        node.setFormat('');
                    }
                });
            }

            // Reset block-level formatting on every paragraph/heading touched by
            // the selection (works for a collapsed caret too): headings/quotes
            // become normal text, and alignment + paragraph spacing are cleared.
            const blockSourceNodes = nodes.length ? nodes : [anchor.getNode()];
            const blocks = new Map();
            for (const node of blockSourceNodes) {
                let block;
                try {
                    block = $getNearestBlockElementAncestorOrThrow(node);
                } catch (e) {
                    continue;
                }
                if (block) blocks.set(block.getKey(), block);
            }
            for (const block of blocks.values()) {
                // With a collapsed caret there is no range to clear precisely,
                // so clear character formatting across the whole block.
                if (isCollapsed) {
                    $clearTextFormattingIn(block);
                }
                if ($isHeadingNode(block) || $isQuoteNode(block)) {
                    // A fresh paragraph has default alignment and no spacing.
                    block.replace($createParagraphNode(), true);
                    continue;
                }
                if (block.getFormatType && block.getFormatType()) {
                    block.setFormat('');
                }
                if (typeof block.getStyle === 'function' && block.getStyle()) {
                    block.setStyle('');
                }
            }
        });
    }, [editor]);

    const openCustomSpacing = () => {
        const lh = parseFloat(state.lineSpacing);
        setCustomLineHeight(Number.isFinite(lh) ? lh : null);
        setCustomBefore(parseFloat(state.spaceBefore) || null);
        setCustomAfter(parseFloat(state.spaceAfter) || null);
        setCustomModalVisible(true);
    };

    const applyCustomSpacing = () => {
        if (editor) {
            if (customLineHeight != null) {
                setLineSpacing(String(customLineHeight));
            }
            applyBlockSpacing({
                'margin-top': customBefore ? `${customBefore}px` : '',
                'margin-bottom': customAfter ? `${customAfter}px` : '',
            });
        }
        setCustomModalVisible(false);
    };

    const handleMenuClick = ({ key }) => {
        if (!editor) {
            setFormatMenuVisible(false);
            return;
        }

        const [group, value] = key.split(':');

        switch (group) {
            case 'text':
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, value);
                break;
            case 'para':
                setBlock(value);
                break;
            case 'align':
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value);
                break;
            case 'indent':
                editor.dispatchCommand(
                    value === 'increase'
                        ? INDENT_CONTENT_COMMAND
                        : OUTDENT_CONTENT_COMMAND,
                    undefined,
                );
                break;
            case 'spacing':
                if (value === 'before') {
                    applyBlockSpacing({
                        'margin-top': state.spaceBefore ? '' : DEFAULT_PARAGRAPH_SPACE,
                    });
                } else if (value === 'after') {
                    applyBlockSpacing({
                        'margin-bottom': state.spaceAfter ? '' : DEFAULT_PARAGRAPH_SPACE,
                    });
                } else if (value === 'custom') {
                    openCustomSpacing();
                } else {
                    setLineSpacing(value);
                }
                break;
            case 'list':
                toggleList(value);
                break;
            case 'clear':
                clearFormatting();
                break;
            default:
                break;
        }

        setFormatMenuVisible(false);
    };

    const formatMenu = (
        <Menu
            onClick={handleMenuClick}
            className="menu-dropdown format-menu"
            selectable={false}
        >
            <Menu.SubMenu
                key="text"
                popupClassName="menu-dropdown format-menu-submenu"
                title={submenuTitle(<FontSizeOutlined />, 'Text')}
            >
                <Menu.Item key="text:bold">
                    {menuItem(<BoldOutlined />, 'Bold', state.isBold)}
                </Menu.Item>
                <Menu.Item key="text:italic">
                    {menuItem(<ItalicOutlined />, 'Italic', state.isItalic)}
                </Menu.Item>
                <Menu.Item key="text:underline">
                    {menuItem(<UnderlineOutlined />, 'Underline', state.isUnderline)}
                </Menu.Item>
                <Menu.Item key="text:strikethrough">
                    {menuItem(
                        <StrikethroughOutlined />,
                        'Strikethrough',
                        state.isStrikethrough,
                    )}
                </Menu.Item>
                <Menu.Item key="text:superscript">
                    {menuItem(
                        <VerticalAlignTopOutlined />,
                        'Superscript',
                        state.isSuperscript,
                    )}
                </Menu.Item>
                <Menu.Item key="text:subscript">
                    {menuItem(
                        <VerticalAlignBottomOutlined />,
                        'Subscript',
                        state.isSubscript,
                    )}
                </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
                key="para"
                popupClassName="menu-dropdown format-menu-submenu"
                title={submenuTitle(<ProfileOutlined />, 'Paragraph styles')}
            >
                {PARAGRAPH_STYLES.map(({ key, label }) => (
                    <Menu.Item key={`para:${key}`}>
                        {menuItem(null, label, state.blockType === key)}
                    </Menu.Item>
                ))}
            </Menu.SubMenu>

            <Menu.SubMenu
                key="align"
                popupClassName="menu-dropdown format-menu-submenu"
                title={submenuTitle(<AlignLeftOutlined />, 'Align & indent')}
            >
                <Menu.Item key="align:left">
                    {menuItem(
                        <AlignLeftOutlined />,
                        'Left',
                        state.elementFormat === 'left' || state.elementFormat === '',
                    )}
                </Menu.Item>
                <Menu.Item key="align:center">
                    {menuItem(
                        <AlignCenterOutlined />,
                        'Center',
                        state.elementFormat === 'center',
                    )}
                </Menu.Item>
                <Menu.Item key="align:right">
                    {menuItem(
                        <AlignRightOutlined />,
                        'Right',
                        state.elementFormat === 'right',
                    )}
                </Menu.Item>
                <Menu.Item key="align:justify">
                    {menuItem(
                        <MenuOutlined />,
                        'Justify',
                        state.elementFormat === 'justify',
                    )}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="indent:increase">
                    {menuItem(<MenuUnfoldOutlined />, 'Increase indent')}
                </Menu.Item>
                <Menu.Item key="indent:decrease">
                    {menuItem(<MenuFoldOutlined />, 'Decrease indent')}
                </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
                key="spacing"
                popupClassName="menu-dropdown format-menu-submenu"
                title={submenuTitle(<ColumnHeightOutlined />, 'Line & paragraph spacing')}
            >
                {LINE_SPACING_OPTIONS.map(({ key, label }) => (
                    <Menu.Item key={`spacing:${key}`}>
                        {menuItem(null, label, state.lineSpacing === key)}
                    </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item key="spacing:before">
                    {menuItem(
                        <VerticalAlignTopOutlined />,
                        'Add space before paragraph',
                        !!state.spaceBefore,
                    )}
                </Menu.Item>
                <Menu.Item key="spacing:after">
                    {menuItem(
                        <VerticalAlignBottomOutlined />,
                        'Add space after paragraph',
                        !!state.spaceAfter,
                    )}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item key="spacing:custom">
                    {menuItem(<VerticalAlignMiddleOutlined />, 'Custom spacing')}
                </Menu.Item>
            </Menu.SubMenu>

            <Menu.SubMenu
                key="list"
                popupClassName="menu-dropdown format-menu-submenu"
                title={submenuTitle(<UnorderedListOutlined />, 'Bullets & numbering')}
            >
                <Menu.Item key="list:number">
                    {menuItem(
                        <OrderedListOutlined />,
                        'Numbered list',
                        state.blockType === 'number',
                    )}
                </Menu.Item>
                <Menu.Item key="list:bullet">
                    {menuItem(
                        <UnorderedListOutlined />,
                        'Bulleted list',
                        state.blockType === 'bullet',
                    )}
                </Menu.Item>
            </Menu.SubMenu>

            <Menu.Divider />

            <Menu.Item key="clear">
                {menuItem(<ClearOutlined />, 'Clear formatting')}
            </Menu.Item>
        </Menu>
    );

    return (
        <>
            <Dropdown
                overlay={formatMenu}
                trigger={['click']}
                visible={formatMenuVisible}
                onVisibleChange={onVisibleChange}
                disabled={isReadOnly || !editor}
                placement={isMobile ? 'rightTop' : undefined}
            >
                <div
                    className="navTabItem"
                    role="menuitem"
                    tabIndex={0}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <span className="nav-tab-trigger">
                        <span className="nav-tab-icon"><FormatPainterOutlined /></span>
                        <span className="nav-tab-label">Format</span>
                        <span className="nav-tab-arrow"><RightOutlined /></span>
                    </span>
                </div>
            </Dropdown>

            <Modal
                title="Custom spacing"
                visible={customModalVisible}
                onOk={applyCustomSpacing}
                onCancel={() => setCustomModalVisible(false)}
                okText="Apply"
                width={320}
                zIndex={10000002}
                centered
            >
                <div className="format-custom-spacing">
                    <label className="format-custom-spacing-row">
                        <span>Line spacing</span>
                        <InputNumber
                            min={0.5}
                            max={10}
                            step={0.1}
                            value={customLineHeight}
                            onChange={setCustomLineHeight}
                            placeholder="1.0"
                        />
                    </label>
                    <label className="format-custom-spacing-row">
                        <span>Before paragraph (px)</span>
                        <InputNumber
                            min={0}
                            max={200}
                            step={1}
                            value={customBefore}
                            onChange={setCustomBefore}
                            placeholder="0"
                        />
                    </label>
                    <label className="format-custom-spacing-row">
                        <span>After paragraph (px)</span>
                        <InputNumber
                            min={0}
                            max={200}
                            step={1}
                            value={customAfter}
                            onChange={setCustomAfter}
                            placeholder="0"
                        />
                    </label>
                </div>
            </Modal>
        </>
    );
};

export default FormatMenu;
